// src/containers/pages/caseManagement/components/CustomerSelector.tsx

import React, { useState, useCallback } from 'react';
import { Box, Autocomplete, TextField, Tooltip } from 'components/mui';
import searchCustomers, { CustomerOption } from 'api/cases/searchCustomers';
import type { SelectedCustomer } from '../types';

interface CustomerSelectorProps {
  customer: SelectedCustomer | null;
  onSelect: (customer: SelectedCustomer | null) => void;
  disabled?: boolean;
}

function CustomerSelector({ customer, onSelect, disabled }: CustomerSelectorProps) {
  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = useCallback(async (keyword: string) => {
    try {
      setLoading(true);
      const results = await searchCustomers(keyword);
      setOptions(results);
    } catch (error) {
      console.error('[CustomerSelector] Search failed:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Convert between PascalCase API type and internal camelCase type
  const currentValue: CustomerOption | null = customer
    ? { Id: customer.id, Name: customer.name }
    : null;

  const wrappedElement = (
    <Autocomplete
      options={options}
      getOptionLabel={(opt: CustomerOption) => opt.Name}
      value={currentValue}
      onChange={(_e: unknown, val: CustomerOption | null) => {
        onSelect(val ? { id: Number(val.Id), name: val.Name } : null);
      }}
      onInputChange={(_e: unknown, val: string) => handleSearch(val)}
      onOpen={() => handleSearch('')}
      isOptionEqualToValue={(opt: CustomerOption, val: CustomerOption) => opt.Id === val.Id}
      loading={loading}
      disabled={disabled}
      renderInput={(params: any) => (
        <TextField
          {...params}
          label="Customer"
          size="small"
          placeholder="Search customers..."
        />
      )}
      sx={{ maxWidth: 500 }}
    />
  );

  if (disabled) {
    return (
      <Tooltip title="Cannot change customer while move is in progress" arrow>
        <Box sx={{ maxWidth: 500 }}>{wrappedElement}</Box>
      </Tooltip>
    );
  }

  return wrappedElement;
}

export default CustomerSelector;
